/**
 * Barra de filtros del portfolio: estado, vertical, dueño y búsqueda, más el
 * contador de resultados.
 *
 * El contador es un `role="status"` y dice las dos cifras — "12 de 31 cuentas" —
 * porque un filtro que no dice sobre cuántas filas quedó es un filtro que
 * esconde el tamaño real del problema.
 *
 * (Carencia anotada del design system: no hay `Select` ni `Field`, así que los
 * controles se estilan acá con las clases de los tokens.)
 */
import { CLIENT_STATE_BY_RISK, CLIENT_STATE_LABELS, type UserRef } from '@ops/contracts'
import type { ReactNode } from 'react'
import { useId } from 'react'

import { Button, Icon } from '../../ui'
import { hayFiltros, type FiltrosPortfolio } from './derivados'

export interface PortfolioFiltrosProps {
  filtros: FiltrosPortfolio
  onChange: (filtros: FiltrosPortfolio) => void
  verticales: readonly string[]
  duenos: readonly UserRef[]
  /** Resumen de resultados, ya formateado. */
  resumen: ReactNode
}

const CLASE_CONTROL =
  'h-7 rounded-md border border-border bg-surface px-2 text-meta text-text hover:border-n300'

export function PortfolioFiltros({
  filtros,
  onChange,
  verticales,
  duenos,
  resumen,
}: PortfolioFiltrosProps) {
  const idEstado = useId()
  const idVertical = useId()
  const idDueno = useId()
  const idBusqueda = useId()

  return (
    <div
      data-testid="portfolio-filtros"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2"
    >
      <div className="flex items-center gap-1.5">
        <label htmlFor={idEstado} className="rotulo">
          Estado
        </label>
        <select
          id={idEstado}
          value={filtros.estado}
          onChange={(evento) => onChange({ ...filtros, estado: evento.target.value })}
          className={CLASE_CONTROL}
        >
          <option value="">Todos</option>
          {CLIENT_STATE_BY_RISK.map((estado) => (
            <option key={estado} value={estado}>
              {CLIENT_STATE_LABELS[estado]}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-1.5">
        <label htmlFor={idVertical} className="rotulo">
          Vertical
        </label>
        <select
          id={idVertical}
          value={filtros.vertical}
          onChange={(evento) => onChange({ ...filtros, vertical: evento.target.value })}
          className={CLASE_CONTROL}
        >
          <option value="">Todas</option>
          {verticales.map((vertical) => (
            <option key={vertical} value={vertical}>
              {vertical}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-1.5">
        <label htmlFor={idDueno} className="rotulo">
          Dueño
        </label>
        <select
          id={idDueno}
          value={filtros.ownerId}
          onChange={(evento) => onChange({ ...filtros, ownerId: evento.target.value })}
          className={CLASE_CONTROL}
        >
          <option value="">Todos</option>
          {duenos.map((dueno) => (
            <option key={dueno.id} value={dueno.id}>
              {dueno.nombre}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-1.5">
        <label htmlFor={idBusqueda} className="rotulo">
          Buscar
        </label>
        <span className="relative inline-flex items-center">
          <Icon
            name="buscar"
            size={12}
            className="pointer-events-none absolute left-2 text-muted"
          />
          <input
            id={idBusqueda}
            type="search"
            value={filtros.q}
            placeholder="Nombre o vertical"
            onChange={(evento) => onChange({ ...filtros, q: evento.target.value })}
            className="h-7 w-52 rounded-md border border-border bg-surface pl-7 pr-2 text-meta text-text placeholder:text-muted hover:border-n300"
          />
        </span>
      </div>

      {hayFiltros(filtros) ? (
        <Button variante="sutil" onClick={() => onChange({ estado: '', vertical: '', ownerId: '', q: '' })}>
          Limpiar filtros
        </Button>
      ) : null}

      <p
        role="status"
        data-testid="portfolio-contador"
        className="ml-auto text-meta text-muted-strong [&_b]:font-semibold [&_b]:text-text"
      >
        {resumen}
      </p>
    </div>
  )
}
